import { proxy } from 'valtio';
import { derive } from 'valtio/utils';

interface CursorVisibilityState {
  /**
   * Whether cursors are hidden due to an anonymous voting session.
   */
  hideCursorsDueToAnonymousVoting: boolean;

  /**
   * Whether cursors are hidden due to user preference.
   */
  hideCursorsDueToUserPreference: boolean;
}

interface CursorVisibilityDerivedState {
  /**
   * Whether cursors are hidden due to any reason.
   */
  hideCursors: boolean;
}

/**
 * The default cursor visibility state when the app is first loaded.
 */
export const defaultCursorVisibilityState: CursorVisibilityState = {
  hideCursorsDueToAnonymousVoting: false,
  hideCursorsDueToUserPreference: false,
};

/**
 * Contains the raw state that determines why cursors might be hidden.
 */
export const cursorVisibilityState = proxy<CursorVisibilityState>({
  hideCursorsDueToAnonymousVoting: false,
  hideCursorsDueToUserPreference: false,
});

/**
 * Contains the derived state that determines whether cursors are hidden.
 */
export const cursorVisibilityDerivedState = derive<
  CursorVisibilityState,
  CursorVisibilityDerivedState
>(
  {
    hideCursors: (get) =>
      get(cursorVisibilityState).hideCursorsDueToAnonymousVoting ||
      get(cursorVisibilityState).hideCursorsDueToUserPreference,
  },
  { sync: true }
);

/**
 * Resets the cursor visibility state to its initial state.
 */
export function resetCursorVisibilityState() {
  cursorVisibilityState.hideCursorsDueToAnonymousVoting = false;
  cursorVisibilityState.hideCursorsDueToUserPreference = false;
}
